/*!
 * Adds the capability to track web performance metrics categorized as Web Vitals. v4.8.2 (https://github.com/snowplow/snowplow-javascript-tracker)
 * Copyright 2022 Snowplow Analytics Ltd, 2010 Anthon Pang
 * Licensed under BSD-3-Clause
 */

import { __assign } from 'tslib';
import { dispatchToTrackersInCollection } from '@snowplow/browser-tracker-core';
import { LOG, buildSelfDescribingEvent, resolveDynamicContext } from '@snowplow/tracker-core';

var WEB_VITALS_SCHEMA = 'iglu:com.snowplowanalytics.snowplow/web_vitals/jsonschema/1-0-0';

/**
 * Attach page listeners to collect the Web Vitals values
 * @param {() => void} callback
 */
function attachWebVitalsPageListeners(callback) {
    // Safari does not fire "visibilitychange" on the tab close
    // So we have 2 options: lose Safari data, or lose LCP/CLS that depends on "visibilitychange" logic.
    // Current solution: if LCP/CLS supported, use `onHidden` otherwise, use `pagehide` to fire the callback in the end.
    //
    // More details: https://github.com/treosh/web-vitals-reporter/issues/3
    var supportedEntryTypes = (PerformanceObserver && PerformanceObserver.supportedEntryTypes) || [];
    var isLatestVisibilityChangeSupported = supportedEntryTypes.indexOf('layout-shift') !== -1;
    if (isLatestVisibilityChangeSupported) {
        var onVisibilityChange_1 = function () {
            if (document.visibilityState === 'hidden') {
                callback();
                window.removeEventListener('visibilitychange', onVisibilityChange_1, true);
            }
        };
        window.addEventListener('visibilitychange', onVisibilityChange_1, true);
    }
    else {
        window.addEventListener('pagehide', callback, { capture: true, once: true });
    }
}
/**
 *
 * @param {string} webVitalsSource Web Vitals script source.
 * @returns {string} The script element of the Web Vitals script. Used for attaching listeners on it.
 */
function createWebVitalsScript(webVitalsSource) {
    var webVitalsScript = document.createElement('script');
    webVitalsScript.setAttribute('src', webVitalsSource);
    webVitalsScript.setAttribute('async', '1');
    webVitalsScript.addEventListener('error', function () {
        LOG.error("Failed to load ".concat(webVitalsSource));
    });
    document.head.appendChild(webVitalsScript);
    return webVitalsScript;
}
/**
 *
 * Adds the Web Vitals measurements on the object used by the trackers to store metric properties.
 * @param {Record<string, unknown>} webVitalsObject
 * @return {void}
 */
function webVitalsListener(webVitalsObject) {
    var _a, _b, _c, _d, _e, _f;
    function addWebVitalsMeasurement(metricSchemaName) {
        return function (arg) {
            webVitalsObject[metricSchemaName] = arg.value;
            webVitalsObject.navigationType = arg.navigationType;
        };
    }
    var webVitals = window.webVitals;
    if (!webVitals) {
        LOG.warn('The window.webVitals API is currently unavailable. web_vitals events will not be collected.');
        return;
    }
    (_a = webVitals.onCLS) === null || _a === void 0 ? void 0 : _a.call(webVitals, addWebVitalsMeasurement('cls'));
    (_b = webVitals.onFID) === null || _b === void 0 ? void 0 : _b.call(webVitals, addWebVitalsMeasurement('fid'));
    (_c = webVitals.onLCP) === null || _c === void 0 ? void 0 : _c.call(webVitals, addWebVitalsMeasurement('lcp'));
    (_d = webVitals.onFCP) === null || _d === void 0 ? void 0 : _d.call(webVitals, addWebVitalsMeasurement('fcp'));
    (_e = webVitals.onINP) === null || _e === void 0 ? void 0 : _e.call(webVitals, addWebVitalsMeasurement('inp'));
    (_f = webVitals.onTTFB) === null || _f === void 0 ? void 0 : _f.call(webVitals, addWebVitalsMeasurement('ttfb'));
}

var _trackers = {};
var WEB_VITALS_SOURCE = 'https://unpkg.com/web-vitals@4/dist/web-vitals.iife.js';
var listenersAttached = false;
var defaultPluginOptions = {
    loadWebVitalsScript: true,
    webVitalsSource: WEB_VITALS_SOURCE,
    context: [],
};
/**
 * Adds Web Vitals measurement events
 *
 * @param pluginOptions.loadWebVitalsScript - Should the plugin immediately load the Core Web Vitals measurement script from UNPKG CDN.
 * @param pluginOptions.webVitalsSource - The URL endpoint the Web Vitals script should be loaded from. Defaults to the UNPKG CDN.
 * @remarks
 */
function WebVitalsPlugin(pluginOptions) {
    if (pluginOptions === void 0) { pluginOptions = defaultPluginOptions; }
    var webVitalsObject = {};
    var options = __assign(__assign({}, defaultPluginOptions), pluginOptions);
    var trackerId;
    var webVitalsScript;
    return {
        activateBrowserPlugin: function (tracker) {
            trackerId = tracker.id;
            _trackers[trackerId] = tracker;
            function sendWebVitals() {
                if (!Object.keys(webVitalsObject).length) {
                    return;
                }
                dispatchToTrackersInCollection(Object.keys(_trackers), _trackers, function (t) {
                    var _a;
                    t.core.track(buildSelfDescribingEvent({
                        event: {
                            schema: WEB_VITALS_SCHEMA,
                            data: webVitalsObject,
                        },
                    }), resolveDynamicContext((_a = options.context) !== null && _a !== void 0 ? _a : [], webVitalsObject));
                });
            }
            if (options.loadWebVitalsScript) {
                webVitalsScript = createWebVitalsScript(options.webVitalsSource);
            }
            /*
             * Attach page listeners only once per page.
             * Prevent multiple trackers from attaching listeners multiple times.
             */
            if (!listenersAttached) {
                if (webVitalsScript) {
                    webVitalsScript.addEventListener('load', function () { return webVitalsListener(webVitalsObject); });
                }
                else {
                    webVitalsListener(webVitalsObject);
                }
                attachWebVitalsPageListeners(sendWebVitals);
                listenersAttached = true;
            }
            return;
        },
    };
}

export { WebVitalsPlugin };
//# sourceMappingURL=index.module.js.map
